import * as React from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorMessage, Field, FormikProvider, useFormik } from 'formik';
import { Grid, Divider, Select, MenuItem, SelectProps } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../../../reduxService/hooks';
import * as Yup from 'yup';
import { setMesh, setReadOnly } from '../../../store/project/projectslice';
import { selectProjectInfo, selectMacro2} from '../../../../store/projectInfo/projectInfoSlice';
import './Mesh.scss';
import { IMeshProps, IMeshForm } from './IMesh';
import { conversionLabel, convertTo, EConversionSIToImperial } from '../../../../conversion';
import { getMeshTypes } from '../../../../api/products/BeMaccaferriProductsHelper';
import { IMeshTypeResponse } from '../../../../api/IApiResponse';
import { updateValidation } from '../../../../Macro2/store/project/resultsSlice';
import { RootState } from '../../../../../reduxService';
import FieldNumber from '../../../../../components/FieldNumber/FieldNumber';

// Define the initial values for the Mesh form
export const MeshDefault: IMeshForm = {
    meshType: 0,
    meshTypeName: "",
    tensileResistance: 180,
    steelMeshUnitWeight: 2.20,
};

const MuiSelect = (props: SelectProps) => (
    <Select {...props} />
);


const Mesh = (props: IMeshProps) => {
    const { t } = useTranslation();
    const projectInfo = useAppSelector(selectProjectInfo);
    const macro2 = useAppSelector(selectMacro2);
    const MeshSchema = Yup.object().shape({
        meshType: Yup.number().required().min(1, `${t("yup.mixed.required")}`).label(t('mesh_macro2_type', { unit_measure: '' })),
        meshTypeName: Yup.string(),
        tensileResistance: Yup.number().required().label(t('mesh_macro2_tensile_resistence', { unit_measure: '' })),
        steelMeshUnitWeight: Yup.number().required().label(t('mesh_macro2_steel_unit_weight', { unit_measure: '' })),
    });

    const checkConversion = (value: number, conversion: EConversionSIToImperial) => {
        return projectInfo.data?.unit == 'Metric' ? value : convertTo(value, conversion);
    }

    const getMeasurementUnit = (unit: string[]) => {
        return ' [' + unit[projectInfo.data?.unit == 'Metric' ? 0 : 1] + ']';
    }

    const dispatch = useAppDispatch();

    const dispatchMesh = (values: IMeshForm) => {
        dispatch(setMesh(values));
    }

    const formikProps = useFormik({
        initialValues: macro2.project?.data?.mesh ?? MeshDefault,
        validationSchema: MeshSchema,
        onSubmit: (values, { setSubmitting }) => {
            dispatchMesh(values);
            setSubmitting(false);
        },
    });

    const handleValueChanged = (valueKey: string, value: any) => {
        formikProps.setFieldValue(valueKey, value);
        if (valueKey === 'meshType') {
            const meshType = MeshTypes.filter(mt => mt.id == value)[0];
            let tensileResistance = meshType.tensileResistance;
            let steelMeshUnitWeight = meshType.steelUnitWeight;
            formikProps.setFieldValue('meshTypeName', meshType.name);
            formikProps.setFieldValue('tensileResistance', tensileResistance);
            formikProps.setFieldValue('steelMeshUnitWeight', steelMeshUnitWeight);
        }
        dispatch(setReadOnly(false));
        dispatchMesh(formikProps.values);
    }

    const [MeshTypes, setMeshTypes] = React.useState<IMeshTypeResponse[]>([]);
    useEffect(() => {
        // React advises to declare the async function directly inside useEffect
        async function getMeshTypesAsync() {
            if( !!projectInfo.data?.unit){
                try{
                    const respData = (await getMeshTypes()).data;
                    setMeshTypes(respData);
        
                    const meshType = respData.find(el => el.name == 'Steelgrid HR 30') ?? respData[0];
                    if (formikProps.values.meshType == 0 && meshType){
                        let tensileResistance = meshType.tensileResistance;
                        let steelMeshUnitWeight = meshType.steelUnitWeight;
                        formikProps.setFieldValue('meshType', meshType.id);
                        formikProps.setFieldValue('meshTypeName', meshType.name);
                        formikProps.setFieldValue('tensileResistance', tensileResistance);
                        formikProps.setFieldValue('steelMeshUnitWeight', steelMeshUnitWeight);
                    } else if (macro2?.project?.data?.readOnly) {
                        formikProps.setFieldValue('meshType', 0);
                    }
                } catch (error) {
                    console.error("getMacro2MeshTypes", error);
                }
            }
        };

        if (!MeshTypes || MeshTypes.length == 0) {
            getMeshTypesAsync();
        }
    }, [projectInfo.data?.unit, window.localStorage.getItem("i18nextLng")]);

    useEffect(() => {
        dispatchMesh(formikProps.values);
    }, [formikProps.values]);

    const currentResults = useAppSelector((state: RootState) => {
        return { ...state.Macro2.results.data };
    });
    useEffect(() => {
        if (currentResults.validation?.startValidation && !currentResults.validation?.mesh) {
            formikProps.submitForm();
            formikProps.validateForm().then((errors) => {
                if (currentResults.validation)
                    dispatch(updateValidation({
                        ...currentResults.validation,
                        mesh: true,
                        meshErrors: Object.keys(errors).length > 0,
                    }));
            });

        }
    }, [JSON.stringify(currentResults.validation)]);

    return (
        <Grid container style={{ alignItems: 'center' }} className='style-form'>
            <FormikProvider value={formikProps}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <label className='form-section-title'>{t('mesh_macro2_section_type')}</label>
                    </Grid>
                    <Grid item xs={12}>
                        <label className='formLabel'>{t('mesh_macro2_type')}</label>
                        <Field
                            as={MuiSelect} name="meshType" value={formikProps.values.meshType}
                            onChange={(e: any) => handleValueChanged('meshType', e.target.value)}>
                            {MeshTypes.map(mt => {
                                return (<MenuItem key={mt.id} value={mt.id}>{mt.name}</MenuItem>)
                            })}
                        </Field>
                        <ErrorMessage name='meshType' component='span' className="text-danger" />
                    </Grid>

                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12}>
                        <label className='form-section-title'>{t('mesh_macro2_section_properties')}</label>
                    </Grid>

                    <Grid item xs={12}>
                        <label className='formLabel'>{t('mesh_macro2_tensile_resistence', { unit_measure: getMeasurementUnit(conversionLabel.kNm_lbfft) })}</label>
                        <FieldNumber disabled step={0.01} name='tensileResistance' className='d-flex justify-content-between align-items-center' readOnly />
                        <ErrorMessage name='tensileResistance' component='span' className="text-danger" />
                    </Grid>

                    <Grid item xs={12}>
                        <label className='formLabel'>{t('mesh_macro2_steel_unit_weight', { unit_measure: getMeasurementUnit(conversionLabel.kgm2_lbfft2) })}</label>
                        <FieldNumber disabled step={0.01} name='steelMeshUnitWeight' className='d-flex justify-content-between align-items-center' readOnly />
                        <ErrorMessage name='steelMeshUnitWeight' component='span' className="text-danger" />
                    </Grid>
                </Grid>
            </FormikProvider>
        </Grid>
        
    );
};

export default Mesh;
